import type { Attachment, AttachmentTarget } from "@gjwtrack/domain";
import { Image } from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TableKit } from "@tiptap/extension-table";
import { Markdown } from "@tiptap/markdown";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, ChevronDown, Code, Image as ImageIcon, Italic, Link as LinkIcon, List, ListChecks, ListOrdered, Paperclip, Quote, SquareCode, Strikethrough, Table as TableIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent, type RefObject } from "react";
import { client, useClient } from "../lib/client.ts";
import { matchUsers, mentionAtCaret, MentionPopup, MentionSuggest, setMentionUsers } from "../lib/mentions.ts";
import { closeMenus, Menu } from "../ui/Menu.tsx";
import { Button } from "../ui/ring.ts";

export interface MarkdownEditorProps {
  initial: string;
  placeholder?: string;
  saveLabel?: string;
  autoFocus?: boolean;
  onSave: (markdown: string) => void | Promise<void>;
  onCancel?: () => void;
  /** Compact: smaller min height (comments). */
  compact?: boolean;
  /** Embedded in a form that has its own buttons (New issue): no Save / Cancel footer. */
  hideFooter?: boolean;
  /** Reports the current Markdown after every edit, for the host's own state (draft autosave). */
  onChange?: (markdown: string) => void;
  /** Fill the parent flex column (New issue dialog): the writing area grows to the space and scrolls inside. */
  fill?: boolean;
  /**
   * Where dropped / picked files are uploaded. A function is called at upload
   * time and may create the owner first — that is how the New issue dialog and
   * a new article attach files: they save their draft to get an id, and the
   * files move to the issue / article when it is created. Without the prop the
   * paperclip is disabled and drops are refused.
   */
  target?: AttachmentTarget | (() => Promise<AttachmentTarget | null>);
}

type Mode = "visual" | "markdown";
const MODE_KEY = "gjw.mdmode";

/**
 * Markdown the visual editor cannot represent: raw HTML (YouTrack imports are
 * full of `<span style>`). Round-tripping it through TipTap escapes it, so such
 * text opens in Markdown mode with Visual locked. `<https://…>` autolinks are
 * fine (they come back as `[url](url)`), and so are tables, task lists and
 * images — the table, list and image extensions serialize them.
 */
const UNSUPPORTED = /<(?!https?:\/\/)[a-zA-Z][^>]*>/m;
export const visualCanShow = (md: string): boolean => !UNSUPPORTED.test(md);

function storedMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "markdown" ? "markdown" : "visual";
  } catch {
    return "visual";
  }
}
function storeMode(m: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    /* storage unavailable: the choice just doesn't stick */
  }
}

/**
 * Description / comment editor that reads and writes Markdown (docs/M4-PLAN.md).
 * Two modes, YouTrack style: Visual (TipTap) and Markdown (plain textarea); the
 * toolbar drives whichever is showing. The last choice is remembered.
 */
export function MarkdownEditor({ initial, placeholder, saveLabel = "Save", autoFocus, onSave, onCancel, compact, hideFooter, onChange, fill, target }: MarkdownEditorProps) {
  const [busy, setBusy] = useState(false);
  const lossy = !visualCanShow(initial);
  const [mode, setMode] = useState<Mode>(() => (lossy ? "markdown" : storedMode()));
  const [text, setText] = useState(initial);
  const ta = useRef<HTMLTextAreaElement>(null);
  // Re-render on caret moves so the block-type label follows the cursor in Markdown mode.
  const [, caretMoved] = useState(0);
  // TipTap's handleKeyDown is created once at mount, so it reads live state through this ref.
  const live = useRef({ mode, busy, target });
  live.current = { mode, busy, target };
  // Markdown-mode edits that don't go through the textarea's onChange (toolbar, uploads) still have to reach the host.
  // `textRef` mirrors `text` synchronously: several uploads land in one tick, each building on the previous insert
  // while React has not re-rendered the textarea yet.
  const textRef = useRef(initial);
  const setMd: Setter = (next) => {
    textRef.current = next;
    setText(next);
    onChange?.(next);
  };
  // Where the next programmatic insert goes while the textarea's own caret is still catching up.
  const caretRef = useRef(0);
  // `@login` autocomplete: the users the current schema knows (handle = login before the @).
  const users = useClient((s) => s.schema?.data.users);
  useEffect(() => {
    setMentionUsers(() => (users ?? []).map((u) => ({ handle: u.login.split("@")[0] ?? u.login, name: u.name })));
  }, [users]);
  const mdPopup = useRef<MentionPopup | null>(null);
  useEffect(() => () => mdPopup.current?.hide(), []);
  const mdMention = (el: HTMLTextAreaElement): void => {
    const at = mentionAtCaret(el);
    const popup = (mdPopup.current ??= new MentionPopup());
    if (!at) {
      popup.hide();
      return;
    }
    const r = el.getBoundingClientRect();
    const rect = new DOMRect(r.left + 12, Math.min(r.bottom, r.top + Math.min(el.scrollHeight, el.clientHeight)) - 8, 0, 0);
    popup.show(matchUsers(at.query), rect, (u) => {
      const next = `${el.value.slice(0, at.start)}@${u.handle} ${el.value.slice(el.selectionStart)}`;
      setMd(next);
      popup.hide();
      const pos = at.start + u.handle.length + 2;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    });
  };

  const editor = useEditor({
    extensions: [StarterKit.configure({ link: false }), Link.configure({ openOnClick: false, autolink: true }), TableKit, Image, TaskList, TaskItem.configure({ nested: true }), Markdown, MentionSuggest],
    content: initial,
    contentType: "markdown",
    autofocus: autoFocus && mode === "visual" ? "end" : false,
    editorProps: {
      attributes: { class: compact ? "min-h-20" : fill ? "min-h-full" : "min-h-40", "data-placeholder": placeholder ?? "" },
      handleKeyDown: (_view, e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          void save();
          return true;
        }
        if (e.key === "Escape") {
          // Esc closes an open toolbar menu (text style) before it cancels the edit.
          if (closeMenus()) return true;
          if (onCancel) {
            onCancel();
            return true;
          }
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor || !onChange) return;
    const h = (): void => onChange(editor.getMarkdown());
    editor.on("update", h);
    return () => {
      editor.off("update", h);
    };
  }, [editor, onChange]);

  // Keep the empty-state placeholder attribute in sync.
  useEffect(() => {
    if (editor) editor.view.dom.querySelector("p")?.classList.toggle("is-editor-empty", editor.isEmpty);
  }, [editor, editor?.state]);

  // Markdown mode sizes to its content like YouTrack's editor: grows with the text up to a
  // viewport-relative cap (CSS max-height), then scrolls inside the box.
  useLayoutEffect(() => {
    const el = ta.current;
    if (!el || fill) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text, mode]);

  const currentMarkdown = (): string => (live.current.mode === "markdown" ? textRef.current : (editor?.getMarkdown() ?? textRef.current));

  // ----------------------------------------------------------------- attachments
  const [uploading, setUploading] = useState(0);
  const [over, setOver] = useState(false);

  /** An uploaded file goes into the text where the caret is: images as Markdown images, anything else as a link. */
  const insertAttachment = (a: Attachment): void => {
    const md = `${a.mime.startsWith("image/") ? "!" : ""}[${a.name}](/api/attachments/${a.id})`;
    if (live.current.mode === "markdown") {
      const el = ta.current;
      const v = textRef.current;
      // Trust the textarea's caret while it still shows the text we last wrote; otherwise our own bookkeeping.
      const at = el && el.value === v ? el.selectionEnd : Math.min(caretRef.current, v.length);
      caretRef.current = at + md.length;
      setMd(v.slice(0, at) + md + v.slice(at));
      if (el) after(el, caretRef.current, caretRef.current);
    } else if (editor) {
      // Insert *after* the selection: insertContent would replace it, and TipTap leaves an inserted
      // image selected, so a second file would eat the first.
      editor.chain().insertContentAt(editor.state.selection.to, md, { contentType: "markdown" }).focus().run();
    }
  };

  /** Upload one by one (each shows up in the owner's attachment list) and reference each as it lands. */
  const uploadFiles = async (files: FileList | File[]): Promise<void> => {
    const spec = live.current.target;
    if (!spec) return;
    // Copy the list before anything awaits: the file input is cleared right after this call,
    // which empties its live FileList.
    const list = Array.from(files);
    // A resolver may have to save a draft first; if that fails there is nowhere to put the files.
    const t = typeof spec === "function" ? await spec().catch(() => null) : spec;
    if (!t || list.length === 0) return;
    for (const f of list) {
      setUploading((n) => n + 1);
      try {
        insertAttachment(await client.uploadAttachment(t, f, f.name));
      } catch {
        /* the client pushes the error toast */
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  // Files only: dragging a selection or an image inside the visual editor is ProseMirror's job, so let those through.
  const dragHasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer.types).includes("Files");
  // Capture phase, so a dropped file never reaches ProseMirror (which would try to insert it as content).
  const dropZone = target
    ? {
        onDragOverCapture: (e: DragEvent) => {
          if (!dragHasFiles(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setOver(true);
        },
        onDragLeaveCapture: (e: DragEvent) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
        },
        onDropCapture: (e: DragEvent) => {
          if (!dragHasFiles(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
          void uploadFiles(e.dataTransfer.files);
        },
      }
    : {};

  const save = async (): Promise<void> => {
    if (live.current.busy) return;
    live.current.busy = true;
    setBusy(true);
    try {
      await onSave(currentMarkdown().trim());
    } finally {
      live.current.busy = false;
      setBusy(false);
    }
  };

  const switchTo = (m: Mode): void => {
    if (m === mode || !editor) return;
    if (m === "markdown") {
      const md = editor.getMarkdown();
      textRef.current = md;
      setText(md);
      caretRef.current = md.length;
    }
    else editor.commands.setContent(text, { contentType: "markdown" });
    setMode(m);
    storeMode(m);
    if (m === "visual") editor.commands.focus("end");
    else requestAnimationFrame(() => ta.current?.focus());
  };

  if (!editor) return null;
  return (
    <div className={`rounded border bg-surface ${over ? "border-accent ring-2 ring-accent/40" : "border-line"} ${fill ? "flex min-h-0 flex-1 flex-col" : ""}`} {...dropZone}>
      <Toolbar editor={editor} mode={mode} lossy={lossy} onMode={switchTo} ta={ta} setText={setMd} canAttach={target !== undefined} uploading={uploading} onFiles={uploadFiles} />
      {mode === "visual" ? (
        <EditorContent editor={editor} className={`md ${fill ? "md-fill min-h-0 flex-1 overflow-y-auto" : ""}`} />
      ) : (
        <textarea
          ref={ta}
          className={`block w-full resize-none overflow-y-auto bg-transparent px-3 py-2 font-mono text-[13px] leading-relaxed outline-none ${fill ? "min-h-0 flex-1" : ""}`}
          style={fill ? undefined : { minHeight: compact ? "5rem" : "14rem", maxHeight: compact ? "50vh" : "70vh" }}
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            setMd(e.target.value);
            mdMention(e.target);
          }}
          onSelect={() => caretMoved((n) => n + 1)}
          onBlur={() => mdPopup.current?.hide()}
          onKeyDown={(e) => {
            if (mdPopup.current?.open && mdPopup.current.handleKey(e.nativeEvent)) {
              e.preventDefault();
              return;
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void save();
            } else if (e.key === "Escape" && !closeMenus()) onCancel?.();
          }}
        />
      )}
      {!hideFooter && (
        <div className="flex items-center gap-2 border-t border-line px-2 py-1.5">
          <Button primary disabled={busy} onClick={() => void save()}>
            {saveLabel}
          </Button>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
          <span className="ml-auto text-xs text-faint">⌘/Ctrl+Enter to {saveLabel.toLowerCase()}</span>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- textarea edits (Markdown mode)

type Setter = (s: string) => void;
type Range = { s: number; e: number; v: string };
const range = (ta: HTMLTextAreaElement): Range => ({ s: ta.selectionStart, e: ta.selectionEnd, v: ta.value });
const after = (ta: HTMLTextAreaElement, s: number, e: number): void => {
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(s, e);
  });
};

/** Wrap the selection (or a placeholder word) in inline delimiters. */
function surround(ta: HTMLTextAreaElement, set: Setter, open: string, close = open, placeholder = "text"): void {
  const { s, e, v } = range(ta);
  const sel = v.slice(s, e) || placeholder;
  set(v.slice(0, s) + open + sel + close + v.slice(e));
  after(ta, s + open.length, s + open.length + sel.length);
}

/** Rewrite every line the selection touches. */
function transformLines(ta: HTMLTextAreaElement, set: Setter, fn: (line: string, i: number) => string): void {
  const { s, e, v } = range(ta);
  const ls = v.lastIndexOf("\n", s - 1) + 1;
  const leRaw = v.indexOf("\n", e);
  const le = leRaw === -1 ? v.length : leRaw;
  const block = v
    .slice(ls, le)
    .split("\n")
    .map((l, i) => fn(l, i))
    .join("\n");
  set(v.slice(0, ls) + block + v.slice(le));
  after(ta, ls, ls + block.length);
}

/** Insert a block snippet on its own lines and put the caret after it. */
function insertBlock(ta: HTMLTextAreaElement, set: Setter, snippet: string): void {
  const { s, e, v } = range(ta);
  const nlBefore = s === 0 || v[s - 1] === "\n" ? "" : "\n\n";
  const nlAfter = e === v.length || v[e] === "\n" ? "" : "\n\n";
  set(v.slice(0, s) + nlBefore + snippet + nlAfter + v.slice(e));
  const p = s + nlBefore.length + snippet.length;
  after(ta, p, p);
}

const HEADING_RE = /^(#{1,6})\s+/;
/** Heading level of the line under the caret, 0 for a paragraph. */
function headingAtCaret(ta: HTMLTextAreaElement | null): number {
  if (!ta) return 0;
  const v = ta.value;
  const ls = v.lastIndexOf("\n", ta.selectionStart - 1) + 1;
  const m = HEADING_RE.exec(v.slice(ls, ls + 8));
  return m ? m[1]!.length : 0;
}

// ----------------------------------------------------------------- toolbar

const BLOCKS: { level: 0 | 1 | 2 | 3; label: string }[] = [
  { level: 0, label: "Normal text" },
  { level: 1, label: "Heading 1" },
  { level: 2, label: "Heading 2" },
  { level: 3, label: "Heading 3" },
];

function Toolbar({
  editor,
  mode,
  lossy,
  onMode,
  ta,
  setText,
  canAttach,
  uploading,
  onFiles,
}: {
  editor: Editor;
  mode: Mode;
  lossy: boolean;
  onMode: (m: Mode) => void;
  ta: RefObject<HTMLTextAreaElement | null>;
  setText: Setter;
  canAttach: boolean;
  uploading: number;
  onFiles: (files: FileList | File[]) => void | Promise<void>;
}) {
  const visual = mode === "visual";
  const file = useRef<HTMLInputElement>(null);
  const B = ({ on, active, title, disabled, children }: { on: () => void; active?: boolean; title: string; disabled?: boolean; children: React.ReactNode }) => (
    <button
      type="button"
      className={`icon-btn ${active ? "bg-sunken text-ink" : ""} disabled:cursor-not-allowed disabled:opacity-35`}
      title={title}
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) on();
      }}
    >
      {children}
    </button>
  );
  // A chain captures the transaction when created, so build it inside each handler, never at render time.
  const c = () => editor.chain().focus();
  // Each button does the TipTap command in Visual mode and the textarea edit in Markdown mode.
  const act = (vis: () => void, md: (t: HTMLTextAreaElement) => void) => () => (visual ? vis() : ta.current && md(ta.current));
  const is = (name: string, attrs?: Record<string, unknown>): boolean => visual && editor.isActive(name, attrs);

  const setImage = act(
    () => {
      const url = window.prompt("Image URL", "https://");
      if (url) c().setImage({ src: url }).run();
    },
    (t) => surround(t, setText, "![", "](https://)", "alt text"),
  );

  const setLink = act(
    () => {
      const prev = editor.getAttributes("link").href as string | undefined;
      const url = window.prompt("Link URL", prev ?? "https://");
      if (url === null) return;
      if (url === "") editor.chain().focus().unsetLink().run();
      else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    },
    (t) => surround(t, setText, "[", "](https://)"),
  );

  const currentBlock = visual ? BLOCKS.find((b) => b.level > 0 && editor.isActive("heading", { level: b.level }))?.level ?? 0 : headingAtCaret(ta.current);
  const setBlock = (level: 0 | 1 | 2 | 3): void => {
    act(
      () => (level === 0 ? c().setParagraph().run() : c().setHeading({ level }).run()),
      (t) => transformLines(t, setText, (l) => (level === 0 ? l.replace(HEADING_RE, "") : `${"#".repeat(level)} ${l.replace(HEADING_RE, "")}`)),
    )();
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-line px-1.5 py-1">
      <Menu
        anchor={
          <button type="button" className="flex h-7 items-center gap-1 rounded px-2 text-[13px] hover:bg-sunken" title="Text style" onMouseDown={(e) => e.preventDefault()}>
            {BLOCKS.find((b) => b.level === currentBlock)?.label ?? "Normal text"}
            <ChevronDown size={14} className="text-muted" />
          </button>
        }
        items={BLOCKS.map((b) => ({ key: String(b.level), label: b.label, onClick: () => setBlock(b.level) }))}
      />
      <span className="mx-1 h-4 w-px bg-line" />
      <B on={act(() => c().toggleBold().run(), (t) => surround(t, setText, "**"))} active={is("bold")} title="Bold (⌘B)"><Bold size={16} /></B>
      <B on={act(() => c().toggleItalic().run(), (t) => surround(t, setText, "*"))} active={is("italic")} title="Italic (⌘I)"><Italic size={16} /></B>
      <B on={act(() => c().toggleStrike().run(), (t) => surround(t, setText, "~~"))} active={is("strike")} title="Strikethrough"><Strikethrough size={16} /></B>
      <span className="mx-1 h-4 w-px bg-line" />
      <B on={act(() => c().toggleBlockquote().run(), (t) => transformLines(t, setText, (l) => `> ${l}`))} active={is("blockquote")} title="Quote"><Quote size={16} /></B>
      <B on={act(() => c().toggleCode().run(), (t) => surround(t, setText, "`", "`", "code"))} active={is("code")} title="Inline code"><Code size={16} /></B>
      <B on={setLink} active={is("link")} title="Link"><LinkIcon size={16} /></B>
      <B on={act(() => c().toggleBulletList().run(), (t) => transformLines(t, setText, (l) => `- ${l}`))} active={is("bulletList")} title="Bullet list"><List size={16} /></B>
      <B on={act(() => c().toggleOrderedList().run(), (t) => transformLines(t, setText, (l, i) => `${i + 1}. ${l}`))} active={is("orderedList")} title="Numbered list"><ListOrdered size={16} /></B>
      <B on={act(() => c().toggleTaskList().run(), (t) => transformLines(t, setText, (l) => `- [ ] ${l}`))} active={is("taskList")} title="Task list"><ListChecks size={16} /></B>
      <span className="mx-1 h-4 w-px bg-line" />
      <B on={act(() => c().toggleCodeBlock().run(), (t) => surround(t, setText, "```\n", "\n```", "code"))} active={is("codeBlock")} title="Code block"><SquareCode size={16} /></B>
      <B on={act(() => c().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run(), (t) => insertBlock(t, setText, "| Column | Column |\n| --- | --- |\n|  |  |"))} active={is("table")} title="Table"><TableIcon size={16} /></B>
      <B on={setImage} active={is("image")} title="Image"><ImageIcon size={16} /></B>

      <div className="ml-auto flex items-center gap-1">
        {uploading > 0 && <span className="text-xs text-muted">uploading {uploading}…</span>}
        {/* Segmented control: the selected half is filled with the accent so it reads at a glance. */}
        <div className="flex rounded border border-line bg-sunken p-0.5" role="group" aria-label="Editor mode">
          <button
            type="button"
            aria-pressed={visual}
            className={`rounded px-2.5 py-0.5 text-[13px] ${visual ? "bg-accent font-medium text-accent-ink" : "text-muted hover:text-ink"} disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted`}
            disabled={lossy}
            title={lossy ? "This text has HTML, tables, task lists or images the visual editor would drop — edit it as Markdown." : "Visual editor"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onMode("visual")}
          >
            Visual
          </button>
          <button
            type="button"
            aria-pressed={!visual}
            className={`rounded px-2.5 py-0.5 text-[13px] ${!visual ? "bg-accent font-medium text-accent-ink" : "text-muted hover:text-ink"}`}
            title="Edit the Markdown source"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onMode("markdown")}
          >
            Markdown
          </button>
        </div>
        {/*
          Safari ignores a programmatic click on a `display: none` file input and will not
          open the picker inside a mousedown that called preventDefault — so this one is
          visually hidden but present (sr-only), and the label carries the click itself
          instead of a button calling .click().
        */}
        <label
          className={`icon-btn cursor-pointer ${canAttach ? "" : "pointer-events-none opacity-35"}`}
          title={canAttach ? "Attach files — or drop them into the editor" : "Attachments are not available here."}
          aria-label="Attach files"
        >
          <Paperclip size={16} />
          <input
            ref={file}
            type="file"
            multiple
            disabled={!canAttach}
            className="sr-only"
            data-test="editor-attach"
            onChange={(e) => {
              if (e.target.files?.length) void onFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  );
}
